import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "./ThemeProvider";
import { Toaster } from "sonner";
import Navigation from "./Navigation";
import Footer from "./Footer";
import FloatingChat from "./FloatingChat";
import ScrollProgress from "./ScrollProgress";

const HeroIcon = ({ icon: Icon, accent }) => (
  <div className="relative w-full max-w-[260px] aspect-square mx-auto" data-testid="legal-hero-icon">
    {/* Concentric pulse rings */}
    <motion.span
      aria-hidden
      className="absolute inset-0 rounded-full"
      style={{ border: `1px solid ${accent}55` }}
      animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0, 0.6] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut" }}
    />
    <motion.span
      aria-hidden
      className="absolute inset-0 rounded-full"
      style={{ border: `1px solid ${accent}55` }}
      animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
      transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut", delay: 0.8 }}
    />

    {/* Rotating accent ring */}
    <motion.div
      aria-hidden
      className="absolute inset-3 rounded-full"
      style={{
        background: `conic-gradient(from 0deg, transparent 0%, ${accent} 25%, transparent 50%, ${accent}88 75%, transparent 100%)`,
        maskImage:
          "radial-gradient(circle, transparent 62%, black 64%, black 67%, transparent 69%)",
        WebkitMaskImage:
          "radial-gradient(circle, transparent 62%, black 64%, black 67%, transparent 69%)",
      }}
      animate={{ rotate: 360 }}
      transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
    />

    {/* Soft gradient blob */}
    <div
      aria-hidden
      className="absolute inset-8 rounded-full blur-2xl opacity-60"
      style={{
        background: `radial-gradient(circle at 30% 30%, ${accent}66, transparent 60%)`,
      }}
    />

    {/* Glass card with icon */}
    <motion.div
      initial={{ scale: 0.92, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-8 rounded-[2rem] dark:glass-dark glass-light shadow-2xl flex items-center justify-center"
      style={{ borderColor: `${accent}33` }}
    >
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
        className="relative"
      >
        <span
          aria-hidden
          className="absolute inset-0 -m-4 rounded-full blur-2xl opacity-70"
          style={{ background: `${accent}40` }}
        />
        <Icon
          className="relative h-20 w-20 md:h-24 md:w-24"
          style={{ color: accent, filter: `drop-shadow(0 6px 18px ${accent}66)` }}
          strokeWidth={1.6}
        />
      </motion.div>
    </motion.div>

    {/* Orbiting dot */}
    <motion.div
      aria-hidden
      className="absolute inset-0"
      animate={{ rotate: 360 }}
      transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
    >
      <span
        className="absolute top-0 left-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full shadow-lg"
        style={{
          background: accent,
          boxShadow: `0 0 14px ${accent}, 0 0 28px ${accent}66`,
        }}
      />
    </motion.div>
  </div>
);

const PageHeader = ({ kicker, title, accent, icon, children }) => (
  <div className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden">
    {/* BG glow */}
    <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-96 w-[40rem] rounded-full bg-[#FF7A00]/15 blur-[140px] pointer-events-none" />
    <div className="absolute top-32 -right-32 h-72 w-72 rounded-full bg-[#00C853]/15 blur-[140px] pointer-events-none" />

    <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground hover:text-[#FF7A00] transition-colors mb-6"
        data-testid="legal-back-home"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to home
      </Link>

      <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="md:col-span-7 space-y-4"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: accent || "#FF7A00" }}
            />
            {kicker}
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            {title}
          </h1>
          {children && (
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
              {children}
            </p>
          )}
          <div className="text-xs text-muted-foreground pt-2">
            Last updated: {new Date().toLocaleDateString("en-IN", { month: "long", day: "numeric", year: "numeric" })}
          </div>
        </motion.div>

        {icon && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5"
          >
            <HeroIcon icon={icon} accent={accent || "#FF7A00"} />
          </motion.div>
        )}
      </div>
    </div>
    <div className="mt-10 max-w-5xl mx-auto px-6 md:px-10">
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
    </div>
  </div>
);

export const LegalShell = ({ kicker, title, intro, accent, icon, children, testid }) => {
  const { theme } = useTheme();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  return (
    <div className="min-h-screen bg-background text-foreground antialiased" data-testid={testid}>
      <ScrollProgress />
      <Navigation />
      <main>
        <PageHeader kicker={kicker} title={title} accent={accent} icon={icon}>
          {intro}
        </PageHeader>
        <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16 relative">
          {children}
        </section>
      </main>
      <Footer />
      <FloatingChat />
      <Toaster
        theme={theme}
        position="bottom-right"
        richColors
        closeButton
        toastOptions={{ classNames: { toast: "rounded-xl border border-border" } }}
      />
    </div>
  );
};

export default LegalShell;
